// src/services/helpDeskService.js

import { repo } from "../dbservice/repo";
import { HelpDeskTicketModel } from "../models/HelpDeskTicketModel";
import { preferencesService } from "./preferencesService";
import { singleValueListService } from "./singleValueListService";

// ─── Role Constants ───────────────────────────────────────────────────────────
export const ROLES = Object.freeze({
  USER:    "user",
  AUDITOR: "auditor",
  ADMIN:   "admin",
});

// ─── Role Configuration (asset scoping only) ─────────────────────────────────
const ROLE_CONFIG = {
  [ROLES.USER]: {
    assetScope: "assigned",
    assetFilters: (ctx) => {
      const filters = [
        { field: "assignedTo",    value: ctx.userId },
        { field: "assignedToUid", value: ctx.userId },
      ];
      if (ctx.email) {
        filters.push({ field: "assignedToName", value: ctx.email });
      }
      return filters;
    },
    defaultPriority: "Medium",
    canRaiseForUnassigned: false,
  },
  [ROLES.AUDITOR]: {
    assetScope: "all",
    assetFilters: (ctx) =>
      ctx.locationId ? [{ field: "location_id", value: ctx.locationId }] : [],
    defaultPriority: "High",
    canRaiseForUnassigned: true,
  },
  [ROLES.ADMIN]: {
    assetScope: "all",
    assetFilters: () => [],
    defaultPriority: "Medium",
    canRaiseForUnassigned: true,
  },
};

// ─── Internal Helpers ────────────────────────────────────────────────────────
const firstNonEmpty = (...candidates) => {
  for (const c of candidates) {
    if (c !== null && c !== undefined) {
      const s = String(c).trim();
      if (s) return s;
    }
  }
  return "";
};

const isDeleted = (ticket) => ticket?.metadata?.deletedFlag === true;

const withoutDeleted = (tickets) =>
  (Array.isArray(tickets) ? tickets : []).filter((t) => !isDeleted(t));

class HelpDeskService {
  constructor() {
    this.collectionName = "helpDeskIssues";
    this.assetsCollectionName = "assets";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = window.localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[HelpDeskService] Failed to parse userData", e);
      return null;
    }
  }

  _validateRole(role) {
    if (!Object.values(ROLES).includes(role)) {
      throw new Error(
        `Invalid role: "${role}". Must be one of: ${Object.values(ROLES).join(", ")}.`,
      );
    }
    return ROLE_CONFIG[role];
  }

  _validateContext(ctx, requiredKeys = ["companyId"]) {
    for (const key of requiredKeys) {
      if (!ctx?.[key]) {
        throw new Error(`Missing required context field: "${key}".`);
      }
    }
  }

  async resolveAdminContext() {
    if (typeof window === "undefined") {
      throw new Error("Not authenticated. Please log in.");
    }
    const raw = window.localStorage.getItem("companyData");
    if (!raw) throw new Error("Not authenticated. Please log in.");

    let companyData;
    try {
      companyData = JSON.parse(raw);
    } catch {
      throw new Error("Invalid session data. Please log in again.");
    }

    if (!companyData?.id) throw new Error("Not authenticated. Please log in.");

    return {
      uid:       companyData.adminUid || companyData.id,
      companyId: companyData.id,
      email:     companyData.email || "Admin",
      role:      ROLES.ADMIN,
    };
  }

  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Ticket ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt || new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || "system",
          },
        };
      }
    }

    const instance = new HelpDeskTicketModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid help desk ticket payload: companyId, userId, issueType and description are required; raisedBy/priority/status must be valid enum values."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    await repo.upsertData(this.collectionName, targetId, payload);
    return { success: true, id: targetId };
  }

  async fetchById(id) {
    if (!id) {
      console.error("[HelpDeskService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[HelpDeskService] Failed to fetch ticket ${id}:`, error);
      return null;
    }
  }

  async fetchIssues(companyId, statusFilter = "All") {
    const allIssues = await repo.fetchData(
      this.collectionName,
      [{ field: "companyId", value: companyId }],
      [],
      null,
    );
    const visible = withoutDeleted(allIssues);
    if (statusFilter === "All") return visible;
    return visible.filter((issue) => issue.status === statusFilter);
  }

   async updateIssue(issueId, fields, currentUser = {}) {
    if (!issueId) throw new Error("Ticket ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const existing = await this.fetchById(issueId);
    if (!existing) throw new Error("Ticket not found.");

 // ── EDIT GUARD ──
const actorEmail = String(user?.email || "").trim().toLowerCase();
const actorRole  = String(user?.role  || "").trim().toLowerCase();
const assigneeEmail = String(existing.assignedToEmail || "").trim().toLowerCase();

const isAdmin = actorRole === ROLES.ADMIN || actorRole === "superadmin";
const isAssignee = !!assigneeEmail && assigneeEmail === actorEmail;
const isUnassigned = !assigneeEmail;

const ASSIGNMENT_ONLY_FIELDS = new Set([
  "assignedToUid", "assignedToEmail", "id", "metadata",
]);

// Only consider fields whose values ACTUALLY differ from the stored record.
const _norm = (v) => (v === null || v === undefined ? "" : String(v).trim());
const changedFields = Object.keys(fields || {}).filter((k) => {
  if (k === "id" || k === "metadata") return false; // never count these
  return _norm(fields[k]) !== _norm(existing[k]);
});

// Metadata is only "safe" to pass along with a re-assignment when it does not
// contain anything other than updatedBy (so deletedFlag etc. cannot sneak in).
const metadataKeys = Object.keys(fields?.metadata || {}).filter(
  (k) => k !== "updatedBy",
);
const metadataIsSafe = metadataKeys.length === 0;

// If nothing but assignment/metadata changed, it's an assignment-only edit.
const isAssignmentOnly =
  Object.keys(fields || {}).length > 0 &&
  Object.keys(fields || {}).every(
    (k) =>
      _norm(fields[k]) === _norm(existing[k]) ||
      (ASSIGNMENT_ONLY_FIELDS.has(k) &&
        (k !== "metadata" || metadataIsSafe)),
  );
// Rules:
//  1. Current assignee → can edit everything.
//  2. Admin → can edit freely on UNASSIGNED tickets.
//  3. Admin → on ASSIGNED tickets, can ONLY change assignment (re-assign / unassign).
//  4. Non-admin, non-assignee → blocked entirely.
const allowed =
  isAssignee ||
  (isAdmin && isUnassigned) ||
  (isAdmin && !isUnassigned && isAssignmentOnly);
    if (!allowed) {
      throw new Error(
        isAdmin
          ? "This ticket is already assigned to someone else. As an administrator you " +
            "can only re-assign or un-assign it (edit content is owned by the assignee). " +
            "To edit the content yourself, re-assign the ticket to you first."
          : "Only the currently assigned user may update this ticket. " +
            "Ask an administrator to re-assign it to you if you need to act.",
      );
    }
       // ── STATUS GUARD: assignee OR admin may Resolve/Close ──
    const nextStatus = fields.status;
    if (nextStatus && nextStatus !== existing.status) {
      if (
        (nextStatus === "Resolved" || nextStatus === "Closed") &&
        !isAssignee &&
        !isAdmin
      ) {
        throw new Error(
          `Only the currently assigned technician (or an administrator) can mark a ticket as "${nextStatus}".`,
        );
      }
      if (nextStatus === "Closed" && String(existing.status) !== "Resolved") {
        throw new Error(
          "A ticket must be Resolved before it can be Closed.",
        );
      }
    }

    // ── RE-ASSIGNMENT GUARD: only Admin may change assignment ──
    const isChangingAssignment =
      (fields.assignedToUid !== undefined &&
        String(fields.assignedToUid ?? "") !==
          String(existing.assignedToUid ?? "")) ||
      (fields.assignedToEmail !== undefined &&
        String(fields.assignedToEmail ?? "").trim().toLowerCase() !==
          String(existing.assignedToEmail ?? "").trim().toLowerCase());

    if (isChangingAssignment && !isAdmin) {
      throw new Error(
        "Only an administrator can re-assign or un-assign a ticket.",
      );
    }

   if (isChangingAssignment) {
      const currentStatus = String(existing.status || "Open");
      const reassignable = currentStatus === "Open" || currentStatus === "In Progress";
      if (!reassignable) {
        throw new Error(
          `Cannot re-assign a ticket in "${currentStatus}" status. ` +
            `Re-assignment is only allowed while the ticket is Open or In Progress.`,
        );
      }

      const raisedBy  = existing?.raisedBy  || null;
      const companyId = existing?.companyId || null;

          if (companyId) {
        await this._assertAssigneeIsReceiver(companyId, {
          uid:   fields.assignedToUid   ? String(fields.assignedToUid)   : "",
          email: fields.assignedToEmail ? String(fields.assignedToEmail) : "",
        });
      }
    }

    const payload = {
      ...fields,
      id: issueId,
      metadata: {
        ...(fields.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(issueId, payload, { isCreation: false });
  }
 async deleteTicket(id, deletedBy = "system") {
  if (!id) throw new Error("Ticket ID is required for deletion.");
  try {
    // Soft-delete is an admin/system action — bypass the assignee edit-guard.
    const existing = await this.fetchById(id);
    if (!existing) throw new Error("Ticket not found.");

    const payload = {
      ...existing,
      id,
      metadata: {
        ...(existing.metadata || {}),
        deletedFlag: true,
        updatedBy: deletedBy,
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  } catch (error) {
    console.error(`[HelpDeskService] Failed to soft-delete ticket ${id}:`, error);
    throw error;
  }
}

  // ── FILE PROCESSING ───────────────────────────────────────────────────────

  async convertFileToBase64(file, uploadedByEmail, onProgress) {
    if (!file) throw new Error("No file provided.");
    this.validateFile(file);

    const base64String = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === "function") {
          onProgress(Math.round((e.loaded / e.total) * 90));
        }
      };
      reader.onload = () => {
        if (typeof onProgress === "function") onProgress(100);
        resolve(reader.result);
      };
      reader.onerror = () => reject(new Error("File reading failed."));
      reader.readAsDataURL(file);
    });

    return {
      fileName:    file.name,
      fileSize:    file.size,
      fileType:    file.type,
      downloadURL: base64String,
      uploadedAt:  new Date().toISOString(),
      uploadedBy:  uploadedByEmail || "Admin",
    };
  }

  validateFile(file) {
    const ALLOWED_TYPES = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error("Invalid file type. Allowed: PDF, JPG, PNG, Excel.");
    }
    if (file.size > 700 * 1024) {
      throw new Error("File size must be less than 700KB.");
    }
  }

  openInvoiceFile(invoiceFileUrl) {
    if (!invoiceFileUrl) throw new Error("Invoice file not available.");
    if (typeof window === "undefined") return;

    if (invoiceFileUrl.startsWith("data:")) {
      const commaIndex = invoiceFileUrl.indexOf(",");
      if (commaIndex === -1) throw new Error("Invalid base64 format.");

      const headerPart = invoiceFileUrl.substring(0, commaIndex);
      const base64Data = invoiceFileUrl.substring(commaIndex + 1);
      const mimeMatch  = headerPart.match(/data:([^;]+)/);
      const mimeType   = mimeMatch ? mimeMatch[1] : "application/octet-stream";

      const bytes     = atob(base64Data);
      const byteArray = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) {
        byteArray[i] = bytes.charCodeAt(i);
      }

      const blob = new Blob([byteArray], { type: mimeType });
      const url  = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      return;
    }

    if (
      invoiceFileUrl.startsWith("http://") ||
      invoiceFileUrl.startsWith("https://")
    ) {
      window.open(invoiceFileUrl, "_blank");
      return;
    }

    throw new Error("Unrecognized file URL format.");
  }

  // ── ROLE-BASED ASSET FETCHING ─────────────────────────────────────────────

  async getAssetsForRole(role, context = {}) {
    const config = this._validateRole(role);
    this._validateContext(context, ["companyId"]);

    if (role === ROLES.USER) {
      this._validateContext(context, ["companyId", "userId"]);
    }

    try {
      if (role === ROLES.USER) {
        const { companyId, userId, email } = context;

        const queries = [
          repo.fetchData(this.assetsCollectionName, [
            { field: "companyId", value: companyId },
            { field: "assignedTo", value: userId },
          ]),
          repo.fetchData(this.assetsCollectionName, [
            { field: "companyId", value: companyId },
            { field: "assignedToUid", value: userId },
          ]),
        ];
        if (email) {
          queries.push(
            repo.fetchData(this.assetsCollectionName, [
              { field: "companyId", value: companyId },
              { field: "assignedToName", value: email },
            ])
          );
        }

        const results = (await Promise.all(queries)).flat();
        const unique = results.filter(
          (a, i, self) => i === self.findIndex((x) => x.id === a.id)
        );

        return unique.filter((a) => {
          const s = String(a.status || "").toLowerCase();
          return (
            !s ||
            s.includes("assign") ||
            s.includes("active") ||
            s.includes("in use")
          );
        });
      }

      const baseFilters = [{ field: "companyId", value: context.companyId }];
      const roleFilters =
        role === ROLES.ADMIN ? [] : config.assetFilters(context);

      return await repo.fetchData(
        this.assetsCollectionName,
        [...baseFilters, ...roleFilters],
        [],
        null,
      );
    } catch (err) {
      console.error(`[HelpDeskService] getAssetsForRole(${role}) failed:`, err);
      throw new Error(
        role === ROLES.USER
          ? "Failed to load your assigned assets."
          : "Failed to load assets.",
      );
    }
  }

  async getUserAssignedAssets(companyId, userId) {
    return this.getAssetsForRole(ROLES.USER, { companyId, userId });
  }

  async getAssetsForAuditor(companyId, locationId = null) {
    return this.getAssetsForRole(ROLES.AUDITOR, { companyId, locationId });
  }

     // ── ROLE-BASED TICKET CREATION ────────────────────────────────────────────

  /**
   * Throws if an assignee is not permitted for the ticket's raisedBy role.
   * Empty admin-defined list → unrestricted (no throw).
   *
   * Uses companyId + raisedBy to scope lookups. Fail-open on SVL read errors.
   */
  async _assertAssigneeAllowed(companyId, raisedBy, { uid = "", email = "" } = {}) {
    if (!companyId || !raisedBy) return;
    if (!uid && !email) return; // no assignee being set → nothing to validate

    let allowed = true;
    try {
      allowed = await singleValueListService.isAssigneeAllowedForRole(
        raisedBy,
        { uid, email },
        { companyId },
      );
    } catch (err) {
      console.warn("[HelpDeskService] assignee allow-list check failed:", err);
      allowed = true; // fail-open
    }
    if (allowed) return;

    throw new Error(
      `This user is not in the admin-approved assignee list for "${raisedBy}" tickets. ` +
        `Update the "${raisedBy}" assignee catalog in Settings → Dropdown Catalogs first.`,
    );
  }

    /**
   * Validates an assignee against the Ticket Receivers catalog.
   * Empty catalog → unrestricted (returns silently).
   */
  async _assertAssigneeIsReceiver(companyId, { uid = "", email = "" } = {}) {
    if (!companyId) return;
    if (!uid && !email) return;

    let receivers = [];
    try {
      receivers = await this.getAssignableUsersByReceiverRoles(companyId);
    } catch (err) {
      console.warn("[HelpDeskService] receiver allow-list check failed:", err);
      return; // fail-open
    }
    if (!receivers.length) return; // empty catalog → allow

    const ok = receivers.some((r) =>
      (email && r.email && r.email === String(email).trim().toLowerCase()) ||
      (uid   && r.uid   && String(r.uid) === String(uid)),
    );
    if (ok) return;

    throw new Error(
      "Selected assignee is not an approved ticket receiver. " +
        "Update the Ticket Receivers catalog in Settings → Dropdown Catalogs.",
    );
  } 
  async _assertNoOpenTicketForAsset(companyId, assetId, { bypass = false } = {}) {
    if (bypass) return;
    if (!companyId || !assetId) return;

    let allowed = false;
    try {
      allowed = await preferencesService.isDuplicateTicketAllowed(this.currentUser);
    } catch (err) {
      console.warn("[HelpDeskService] duplicate-ticket pref check failed:", err);
      allowed = true; // fail-open so a read bug never locks users out
    }
    if (allowed) return;

    const existing = await this.fetchIssues(companyId, "All");
    const activeStatuses = new Set(["Open", "In Progress"]);
    const conflict = existing.find(
      (t) =>
        t.assetId &&
        String(t.assetId) === String(assetId) &&
        activeStatuses.has(t.status),
    );

    if (conflict) {
      const conflictRef = conflict.ticketId || conflict.id;
      throw new Error(
        `An open ticket (#${String(conflictRef).slice(0, 8)}) already exists for this asset. ` +
          `Close it before raising a new one, or enable "Allow Multiple Open Tickets per Asset" in Settings → Preferences.`,
      );
    }
  }

   async createTicketForRole(role, payload, currentUser = {}) {
    const config = this._validateRole(role);
    this._validateContext(payload, ["companyId", "userId"]);

    // ── NEW: block duplicates unless the company preference allows it ──
    await this._assertNoOpenTicketForAsset(
      payload.companyId,
      payload.assetId ?? null,
      { bypass: payload.autoCreated === true },
    );

      // ── Validate assignee against the Ticket Receivers catalog ──
    if (payload.assignedToUid || payload.assignedToEmail) {
      await this._assertAssigneeIsReceiver(payload.companyId, {
        uid:   payload.assignedToUid   ? String(payload.assignedToUid)   : "",
        email: payload.assignedToEmail ? String(payload.assignedToEmail) : "",
      });
    }

    const user = currentUser?.email ? currentUser : this.currentUser;
    const priority = payload.priority || config.defaultPriority;

    const targetId = payload.id || payload.ticketId ||
      `hd_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    const resolvedUserEmail = firstNonEmpty(
      payload.userEmail,
      payload.email,
      user?.email,
    );

    const creationPayload = {
      ...payload,
      id:            targetId,
      ticketId:      payload.ticketId || targetId,
      companyId:     payload.companyId || user?.companyId || null,
      userId:        payload.userId || user?.uid || null,
      userEmail:     resolvedUserEmail,
      email:         payload.email || resolvedUserEmail,
      priority,
      raisedBy:      role,
      status:        "Open",
      assignedToUid:   payload.assignedToUid   || null,
      assignedToEmail: payload.assignedToEmail || null,
      metadata: {
        ...(payload.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, creationPayload, { isCreation: true });
  }

  async createMissingAssetTicket({
    companyId,
    auditorId,
    auditorEmail,
    assetId,
    assetName,
    assetTag,
    auditEventId,
    auditEventTitle,
    locationCode,
    remark,
  }) {
    if (!companyId) throw new Error("companyId is required");
    if (!auditorId) throw new Error("auditorId is required");

    if (auditEventId && assetId) {
      const existing = await this.fetchTicketsByAuditEvent(companyId, auditEventId);
      const dup = existing.find(
        (t) =>
          t.assetId === assetId &&
          t.status !== "Resolved" &&
          t.status !== "Closed",
      );
      if (dup) return dup.ticketId || dup.id;
    }

    const description = [
      `Asset reported MISSING during audit "${auditEventTitle || "Unknown Audit"}".`,
      "",
      `Asset:     ${assetName || "—"}`,
      `Tag:       ${assetTag  || "—"}`,
      `Location:  ${locationCode || "—"}`,
      ...(remark?.trim() ? ["", `Auditor note: ${remark.trim()}`] : []),
    ].join("\n");

    const result = await this.createTicketForRole(ROLES.AUDITOR, {
      companyId,
      userId:          auditorId,
      email:           auditorEmail || "",
      userEmail:       auditorEmail || "",
      issueType:       "Missing Asset",
      assetId:         assetId         ?? null,
      assetName:       assetName       ?? null,
      assetTag:        assetTag        ?? null,
      auditEventId:    auditEventId    ?? null,
      auditEventTitle: auditEventTitle ?? null,
      locationCode:    locationCode    ?? null,
      remark:          remark          || "",
      description,
      autoCreated:     true,
    });

    if (!result?.success || !result?.id) {
      throw new Error("Failed to create missing asset ticket.");
    }
    return result.id;
  }

  /** @deprecated Use createTicketForRole(role, payload) instead. */
  async createTicket(payload, currentUser = {}) {
    const role = payload.raisedBy || ROLES.USER;
    return this.createTicketForRole(role, payload, currentUser);
  }

  // ── ROLE-BASED TICKET FETCHING ────────────────────────────────────────────

  async fetchTickets({ companyId = "", userId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) filters.push({ field: "companyId", value: String(companyId) });
    if (status)    filters.push({ field: "status",    value: status });
    if (userId)    filters.push({ field: "userId",    value: userId });

    try {
      const tickets = (await repo.fetchData(this.collectionName, filters)) || [];
      return withoutDeleted(tickets);
    } catch (error) {
      console.error("[HelpDeskService] Failed to fetch tickets:", error);
      return [];
    }
  }

  async getTicketsForRole(role, context = {}) {
    this._validateRole(role);
    this._validateContext(context, ["companyId"]);

    const statusFilter = context.statusFilter || "All";

    try {
      const filters = [{ field: "companyId", value: context.companyId }];

      if (role === ROLES.USER) {
        this._validateContext(context, ["companyId", "userId"]);
        filters.push({ field: "userId", value: context.userId });
      }

      const raw = await repo.fetchData(this.collectionName, filters, [], null);
      const tickets = withoutDeleted(raw);

      if (statusFilter === "All") return tickets;
      return tickets.filter((t) => t.status === statusFilter);
    } catch (err) {
      console.error(`[HelpDeskService] getTicketsForRole(${role}) failed:`, err);
      throw new Error("Failed to load tickets.");
    }
  }

  async fetchUserTickets(companyId, userId, statusFilter = "All") {
    return this.getTicketsForRole(ROLES.USER, { companyId, userId, statusFilter });
  }

  async getUserTickets(companyId, userId) {
    return this.getTicketsForRole(ROLES.USER, { companyId, userId });
  }

  async fetchTicketsByRaisingRole(companyId, raisedByRole, statusFilter = "All") {
    this._validateRole(raisedByRole);
    if (!companyId) throw new Error("Missing companyId.");

    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId", value: companyId    },
          { field: "raisedBy",  value: raisedByRole },
        ],
        [],
        null,
      );
      const tickets = withoutDeleted(raw);
      if (statusFilter === "All") return tickets;
      return tickets.filter((t) => t.status === statusFilter);
    } catch (err) {
      console.error("[HelpDeskService] fetchTicketsByRaisingRole failed:", err);
      throw new Error("Failed to load role-filtered tickets.");
    }
  }

  async fetchTicketsByAuditEvent(companyId, auditEventId) {
    if (!companyId || !auditEventId) {
      throw new Error("Missing companyId or auditEventId.");
    }

    try {
      const raw = await repo.fetchData(
        this.collectionName,
        [
          { field: "companyId",    value: companyId    },
          { field: "auditEventId", value: auditEventId },
        ],
        [],
        null,
      );
      return withoutDeleted(raw);
    } catch (err) {
      console.error("[HelpDeskService] fetchTicketsByAuditEvent failed:", err);
      throw new Error("Failed to load tickets for this audit.");
    }
  }
  // ── ASSIGNEE CATALOG (UI) ───────────────────────────────────────────────
  async getAssignableUsersForRole(raisedBy, companyId) {
    if (!raisedBy || !companyId) return [];
    try {
      return await singleValueListService.getRoleAllowedAssignees(raisedBy, {
        companyId,
      });
    } catch (err) {
      console.error("[HelpDeskService] getAssignableUsersForRole failed:", err);
      return [];
    }
  }

  /**
   * Returns every user whose role appears in the "Ticket Receivers" catalog.
   * Each entry: { uid, email, name, role }
   * Used by the raise-ticket form to populate the assignee dropdown.
   */
     async getAssignableUsersByReceiverRoles(companyId) {
    if (!companyId) return [];
    try {
      const receiverRoles = await singleValueListService.getTicketReceiverRoles({ companyId });
      
      // Handle both { value: 'technician' } and 'technician'
      const roleSet = new Set(
        (Array.isArray(receiverRoles) ? receiverRoles : [])
          .map((r) => {
            const raw = typeof r === 'object' && r !== null ? (r.value || r.label || '') : r;
            return String(raw).trim().toLowerCase();
          })
          .filter(Boolean),
      );
      const useAll = roleSet.size === 0;

      const { userService } = await import("./userService");
      
      // FIX: Added role: "admin" so userService.fetchUsers doesn't bail out early
      const rows = await userService.fetchUsers({
        currentUser: { companyId, role: "admin" }, 
      });
      
      const list = Array.isArray(rows) ? rows : (rows?.data ?? []);

      console.log("[ReceiverCheck] Roles Allowed:", Array.from(roleSet));
      console.log("[ReceiverCheck] Users Found:", list.length);

      return list
        .filter((u) => {
          const r = String(u.role || "").trim().toLowerCase();
          if (!r || r === "user") return false;   // never assign to end-users
          return useAll ? true : roleSet.has(r);
        })
        .map((u) => ({
          uid:   u.uid || u.id || "",
          email: String(u.email || "").trim().toLowerCase(),
          name:  u.name || u.displayName || u.email || "",
          role:  String(u.role || "").trim().toLowerCase(),
        }))
        .filter((u) => u.email);
    } catch (err) {
      console.error("[HelpDeskService] getAssignableUsersByReceiverRoles failed:", err);
      return [];
    }
  }
    // ── TECHNICIAN TICKET FETCHING ──────────────────────────────────────────

    async getTicketsForTechnician(companyId, technicianUid, technicianEmail) {
    if (!companyId) throw new Error("companyId is required");

    const allTickets = await this.fetchIssues(companyId, "All");
    const email = (technicianEmail || "").trim().toLowerCase();
    const uid   = technicianUid ? String(technicianUid) : "";

    // Filter ONLY tickets assigned to this technician
    return allTickets
      .filter((t) => {
        const aEmail = (t.assignedToEmail || "").trim().toLowerCase();
        const aUid   = t.assignedToUid ? String(t.assignedToUid) : "";
        return (
          (email && aEmail && aEmail === email) ||
          (uid   && aUid   && aUid === uid)
        );
      })
      .map((t) => ({ ...t, _canEdit: true })); 
  }

  /**
   * Fetch all assets for a company (used by technicians to
   * resolve warranty / asset details for their tickets).
   */
  async getCompanyAssets(companyId) {
    if (!companyId) return [];
    try {
      return (
        (await repo.fetchData(
          this.assetsCollectionName,
          [{ field: "companyId", value: companyId }],
          [],
          null,
        )) || []
      );
    } catch (err) {
      console.error("[HelpDeskService] getCompanyAssets failed:", err);
      return [];
    }
  }
}

export const helpDeskService = new HelpDeskService();